// Shared one-time password setup / reset page.
// Same component drives /investor/set-password, /banker/set-password,
// /admin/set-password — only the audience prop and the post-success
// redirect path differ.
//
// Reads ?token= from the URL, posts {token, newPassword} to the matching
// /api/{audience-portal}/auth/consume-reset endpoint. On 200, redirects
// the user to their login page with a success banner; on 4xx, surfaces
// the error and offers a "request a new link" path back to login.

import { useEffect, useMemo, useState } from "react";
import { Lock, Eye, EyeOff, Loader2, CheckCircle2, AlertCircle } from "lucide-react";

const BASE = import.meta.env.BASE_URL;

type Audience = "investor" | "banker" | "admin" | "founder" | "mentor";

const AUDIENCE_CONFIG: Record<Audience, {
  label: string;
  consumeEndpoint: string;
  loginPath: string;
}> = {
  investor: {
    label: "Investor",
    consumeEndpoint: "api/investor-portal/auth/consume-reset",
    loginPath: "/investor/login",
  },
  banker: {
    label: "Advisor",
    consumeEndpoint: "api/banker-portal/auth/consume-reset",
    loginPath: "/banker/login",
  },
  admin: {
    label: "Admin",
    consumeEndpoint: "api/admin/auth/consume-reset",
    loginPath: "/admin/login",
  },
  founder: {
    label: "Founder",
    consumeEndpoint: "api/founder-portal/auth/consume-reset",
    loginPath: "/1to10/login",
  },
  mentor: {
    label: "Mentor",
    consumeEndpoint: "api/mentor-portal/auth/consume-reset",
    loginPath: "/mentor/login",
  },
};

interface SetPasswordPageProps {
  audience: Audience;
  // For founder audience, the cohort slug from the URL — used to compute
  // the right post-success login redirect (/<slug>/founder/login). Other
  // audiences ignore this prop.
  cohortSlug?: string;
}

export default function SetPasswordPage({ audience, cohortSlug }: SetPasswordPageProps) {
  const baseConfig = AUDIENCE_CONFIG[audience];
  const config = audience === "founder" && cohortSlug
    ? { ...baseConfig, loginPath: `/${cohortSlug}/founder/login` }
    : baseConfig;
  const token = useMemo(() => {
    const sp = new URLSearchParams(window.location.search);
    return sp.get("token") || "";
  }, []);

  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // No-token guard: don't even render the form. Send them to login.
  useEffect(() => {
    if (!token) setError("This link is missing a token. Request a new one from the login page.");
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!token) return;
    if (newPassword.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (newPassword !== confirm) { setError("Passwords don't match."); return; }

    setLoading(true);
    try {
      const res = await fetch(`${BASE}${config.consumeEndpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, newPassword }),
      });
      if (res.ok) {
        setSuccess(true);
        // Brief pause so the user sees the success state, then bounce to login.
        setTimeout(() => { window.location.replace(config.loginPath + "?reset=success"); }, 1200);
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data?.error || "Could not set your password. The link may have expired.");
      }
    } catch {
      setError("Network error. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="bg-white border border-neutral-200 rounded-2xl shadow-sm p-8">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-[#FF5C00] flex items-center justify-center text-white font-bold font-serif text-lg">F</div>
            <div>
              <p className="text-sm font-semibold text-neutral-900">Favcy Select</p>
              <p className="text-xs text-neutral-500">{config.label} account</p>
            </div>
          </div>
          <h1 className="font-serif text-2xl font-bold text-neutral-900 mt-6 mb-2">Set your password</h1>
          <p className="text-sm text-neutral-600 mb-6">
            Choose a password for your {config.label.toLowerCase()} account. You'll use this every time you sign in.
          </p>

          {success ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-emerald-900 text-sm">Password set</p>
                <p className="text-xs text-emerald-700 mt-1">Redirecting you to sign in…</p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1.5">New password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                  <input
                    type={show ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    autoComplete="new-password"
                    disabled={loading || !token}
                    className="w-full pl-9 pr-10 py-2.5 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF5C00]/40 focus:border-[#FF5C00] disabled:bg-neutral-100"
                  />
                  <button
                    type="button"
                    onClick={() => setShow(!show)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700"
                    tabIndex={-1}
                  >
                    {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1.5">Confirm password</label>
                <input
                  type={show ? "text" : "password"}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Re-type the same password"
                  autoComplete="new-password"
                  disabled={loading || !token}
                  className="w-full px-3 py-2.5 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF5C00]/40 focus:border-[#FF5C00] disabled:bg-neutral-100"
                />
              </div>

              {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-800">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !token}
                className="w-full bg-[#FF5C00] hover:bg-[#E55300] text-white font-semibold text-sm py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Setting password…</> : "Set password and continue"}
              </button>

              <div className="text-center pt-2">
                <a href={config.loginPath} className="text-xs text-neutral-500 hover:text-neutral-800">Back to sign in</a>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
